import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Loader2, ArrowLeft, FolderKanban, Archive, FileBarChart } from "lucide-react";
import api from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import { Button } from "@/components/ui/button";

export default function Strategies() {
  const { selectProject } = useProjects();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/strategies")
      .then((r) => setItems(r.data.strategies || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const open = (projectId) => {
    selectProject(projectId);
    navigate("/app/strategy");
  };

  return (
    <div className="space-y-6" data-testid="strategies-page">
      <div>
        <h1 className="text-3xl font-extrabold font-heading">الاستراتيجيات</h1>
        <p className="text-muted-foreground">كل استراتيجياتك مرتبطة بمشاريعها — منفصلة عن قائمة المشاريع.</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <div className="glass rounded-2xl p-16 text-center border border-border" data-testid="strategies-empty">
          <Sparkles className="w-12 h-12 text-primary mx-auto mb-4" />
          <p className="text-muted-foreground mb-4">لا توجد استراتيجيات بعد. أنشئ مشروعاً «من الصفر» وولّد استراتيجيته.</p>
          <Button onClick={() => navigate("/app/projects")} className="bg-primary hover:bg-primary/90 text-white gap-2" data-testid="strategies-create-project">
            <FolderKanban className="w-4 h-4" /> إلى المشاريع
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((s, i) => (
            <motion.div key={s.project_id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
              className="glass rounded-2xl p-5 border border-border flex flex-col" data-testid={`strategy-card-${s.project_id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <h3 className="font-bold text-lg">{s.project_name}</h3>
                  <p className="text-xs text-muted-foreground">{s.business_type}</p>
                </div>
                <div className="flex items-center gap-1 bg-primary/15 text-primary text-xs px-2 py-1 rounded-full shrink-0">
                  <Sparkles className="w-3 h-3" /> استراتيجية
                </div>
              </div>
              {s.tagline && <p className="text-sm text-accent font-semibold mb-3">“{s.tagline}”</p>}
              {s.colors?.length > 0 && (
                <div className="flex gap-1.5 mb-4">
                  {s.colors.slice(0, 5).map((c, idx) => (
                    <span key={idx} className="w-6 h-6 rounded-md border border-border" style={{ backgroundColor: c }} title={c} />
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2 mt-auto">
                <Button onClick={() => open(s.project_id)} className="flex-1 bg-primary hover:bg-primary/90 text-white gap-1 h-9 text-xs" data-testid={`open-strategy-${s.project_id}`}>
                  فتح الاستراتيجية <ArrowLeft className="w-3.5 h-3.5" />
                </Button>
                <Button onClick={() => navigate(`/app/report/${s.project_id}`)} variant="outline" className="gap-1 h-9 text-xs border-border" data-testid={`strategy-report-${s.project_id}`}>
                  <FileBarChart className="w-3.5 h-3.5" /> التقرير
                </Button>
                {s.project_status === "archived" && (
                  <span className="text-xs text-destructive flex items-center gap-1"><Archive className="w-3.5 h-3.5" /> مؤرشف</span>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
